import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Login() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!loading && user) navigate("/", { replace: true });
    }, [user, loading, navigate]);

    useEffect(() => {
        // If AuthCallback redirected here with an error, surface it.
        const err = location.state?.error || null;
        if (err) setError(err);
    }, [location.state]);

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-[#faf7ef] text-stone-900 relative overflow-hidden">
            {/* Ambient background */}
            <div
                className="absolute inset-0 opacity-25 bg-cover bg-center"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1638184984605-af1f05249a56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')" }}
                aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#faf7ef] via-[#faf7ef]/85 to-[#faf7ef]/65" aria-hidden />

            <div className="relative min-h-screen grid lg:grid-cols-2">
                {/* Left brand column */}
                <div className="hidden lg:flex flex-col justify-between p-12 border-r border-stone-200/70">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-6 w-6 text-emerald-700" strokeWidth={1.5} />
                        <span className="font-display text-2xl tracking-tight">Parity</span>
                    </div>
                    <div>
                        <h1 className="font-display text-5xl leading-[1.05] tracking-tight text-stone-900 max-w-xl">
                            The ledger for<br />
                            <em className="text-emerald-700 not-italic">two-partner</em> diamond desks.
                        </h1>
                        <p className="text-stone-500 mt-6 max-w-md leading-relaxed">
                            Split parcels, allocate cost, log expenses on the go, then let the waterfall model
                            settle capital, expenses and profit — automatically.
                        </p>
                    </div>
                    <div className="text-xs uppercase tracking-widest text-stone-400">Bashari &nbsp;·&nbsp; Ido</div>
                </div>

                {/* Right auth column */}
                <div className="flex items-center justify-center p-6 sm:p-12">
                    <div className="w-full max-w-md">
                        <div className="lg:hidden flex items-center gap-2 mb-10">
                            <Sparkles className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
                            <span className="font-display text-xl">Parity</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Sign in</div>
                        <h2 className="font-display text-3xl sm:text-4xl text-stone-900 leading-tight">
                            Welcome back.
                        </h2>
                        <p className="text-stone-500 mt-3 text-sm leading-relaxed">
                            Use your Google account to access the shared partnership ledger.
                        </p>

                        <div className="mt-10 space-y-3">
                            {error && (
                                <div className="flex items-start gap-2 border border-rose-500/50 bg-rose-50 px-3 py-2.5 text-sm text-rose-700" data-testid="login-error">
                                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                                    <div>{error}</div>
                                </div>
                            )}
                            <Button
                                onClick={handleLogin}
                                data-testid="google-login-button"
                                className="w-full rounded-none bg-stone-900 hover:bg-stone-800 text-stone-50 h-11 font-sans font-medium"
                            >
                                <svg className="h-4 w-4 mr-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                                </svg>
                                Continue with Google
                            </Button>
                            <p className="text-[10px] uppercase tracking-widest text-stone-400 text-center pt-4">
                                Access restricted — invited partners only.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
